import React, { useEffect, useMemo, useRef, useState } from "react";
// LIBRARY IMPORTS
import { Menu } from "@headlessui/react";
import Drawer from "components/ui-component/SideDrawer";
import { useNavigate, useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";

// COMPONENT IMPORTS
import SelectAllDropDown from "components/form/SelectAllDropDown";
import Header from "components/layout/header";
import AmountFilter from "components/payments/AmountFilter";
import CustomTable from "components/ui-component/CustomTable";
import { formatPaymentListDate } from "utils/DateFormat";
import { cardToShortname, encode, formatCurrency } from "utils/HelperFunctions";
import {
  ActionCenterStatusOptions,
  FilterAmountOptions,
  FilterDateOptions,
  TypeFilter,
} from "utils/Options";
import Button from "components/form/Button";
// ICONS IMPORTS
import { EyeIcon } from "icons/payments/EyeIcon";
import { GreenTrueIcon } from "icons/payments/GreenTrueIcon";
import { CheckCircleIcon } from "icons/subscription/CheckCircleIcon";
import { CloseSmRedIcon } from "icons/subscription/CloseSmRedIcon";
import { MoreIcon } from "icons/payments/MoreIcon";
import { ExpiredIcon } from "icons/subscription/ExpiredIcon";
import { PastDueIcon } from "icons/subscription/PastDueIcon";
import { PayNowIcon } from "icons/subscription/PayNoeIcon";
import ActionPreview from "components/actionCenter/ActionPreview";
import { onSetSmallSidebar } from "store/slices/commonSlice";
import { useAppDispatch } from "store/hooks";
import {
  ActionCountGet,
  actionListGet,
  useActionCount,
  useActionList,
} from "store/slices/actionSlice";
import { useUserData } from "store/slices/authSlice";
import NoAction from "icons/action/NoAction";
import QuotesPreview from "components/actionCenter/QuotesPreview";
import InvoicePreview from "components/actionCenter/InvoicePreview";
import DateFilter from "components/subscriptions/DateFilter";
import { decode } from "utils/HelperFunctions";
import CustomTooltip from "components/ui-component/CustomTooltip";
import { InfoRedCircleIcon } from "icons/subscription/InfoRedCircleIcon";
import { useMediaQuery } from "react-responsive";
import SubscriptionsPreview from "components/subscriptions/SubscriptionsPreview";
import {
  ActionRow,
  ActionType,
  AmountRange,
  FilterState,
  FilterType,
  StatusStylesAndIcon,
  ActionCenterColumn,
} from "types/actionCenter";
import { TestIdScope, useTestAttrsFactory, useTid } from "utils/testid";
import { getActionNavigationSlug } from "utils/actionRoute";

const ActionCenterContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const tid = useTid();
  const testAttrs = useTestAttrsFactory();
  const isLessThan590 = useMediaQuery({ query: "(max-width:590px)" });
  const filterScrollRef = useRef<HTMLDivElement>(null);
  const amountFilterRef = useRef<HTMLDivElement>(null);
  const dateFilterRef = useRef<HTMLDivElement>(null);
  const statusFilterRef = useRef<HTMLDivElement>(null);
  const methodFilterRef = useRef<HTMLDivElement>(null);

  const [selectedFilter, setSelectedFilter] = useState<FilterType>("all");
  const [status, setStatus] = useState<string[]>([]);
  const [method, setMethod] = useState<string[]>([]);
  const [paymentType, setPaymentType] = useState<string[]>([]);
  const [amount, setAmount] = useState<AmountRange | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [isPreviewId, setIsPreviewId] = useState<string>("");
  const [isSubscriptionPreview, setIsSubscriptionPreview] =
    useState<boolean>(false);
  const [isSubscriptionPreviewId, setIsSubscriptionPreviewId] =
    useState<number>(0);
  const [view, setView] = useState<string>("");
  const [previewType, setPreviewType] = useState<ActionType | "">(
    "payment_link"
  );
  const [dateInput1, setDateInput1] = useState<string | undefined>(undefined);
  const [dateInput2, setDateInput2] = useState<string | undefined>(undefined);
  const [dateFilter, setdateFilter] = useState<string | undefined>();
  const [isTableLoading, setIsTableLoading] = useState<boolean>(true);

  const [filter, setFilter] = useState<FilterState>({
    skip: 0,
    limit: 20,
    method: undefined,
    status: [],
    amount: undefined,
    startDate: undefined,
    endDate: undefined,
  });

  const navigate = useNavigate();
  const { clusterId: cluster_id } = useParams<{ clusterId: string }>();
  const clusterId = decode(cluster_id || "", true);
  const userData = useUserData();
  const actionCountSummery = useActionCount();
  const actionList = useActionList() || { actionListData: [], totalCount: 0 };
  const actionCount = useMemo(
    () => actionCountSummery?.summary,
    [actionCountSummery?.summary]
  );

  const getActionRowKey = (row: ActionRow) =>
    `${row?.action || "action"}-${row?.id || row?.url_slug || row?.invoice_number || row?.quote_number || row?.subscription_id || "unknown"}`;

  const getActionLabel = (row: ActionRow) => {
    if (row?.action === "payment_link") return "payment link";
    if (row?.action === "invoice") return "invoice";
    if (row?.action === "quotes") return "quote";
    return "payment agreement";
  };

  // Agreements that are still due show their offer expiry, the same date the
  // agreement page shows; other actions keep their due date. Rows without a
  // due date (e.g. completed agreements) keep showing "--".
  const getActionDueDate = (row: ActionRow) =>
    row?.due && row?.action === "agreement"
      ? row?.expiry_date || row?.due
      : row?.due;

  const onGetActionCount = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const payload = {
        accounts_id: userData?.data?.accountData?.id,
      };
      await dispatch(ActionCountGet(payload)).unwrap();
      setIsLoading(false);
    } catch (error: unknown) {
      setIsLoading(false);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      console.log("get count err: -->", errorMessage);
    }
  };

  const onGetActionList = async (): Promise<void> => {
    setIsTableLoading(true);
    try {
      const payload = {
        ...filter,
        accounts_id: userData?.data?.accountData?.id,
      };
      await dispatch(actionListGet(payload)).unwrap();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      console.log("get payment history err: -->", errorMessage);
    } finally {
      setIsTableLoading(false);
    }
  };

  const getStatusStylesAndIcon = (
    status: string | null | undefined
  ): StatusStylesAndIcon => {
    if (status?.trim() !== "") {
      switch (status?.toLowerCase()) {
        case "sent":
        case "open":
        case "opened":
        case "pending":
          return {
            className: "bg-[#f3f8fe] text-blue",
            Icon: CheckCircleIcon,
            label: "Open",
          };
        case "refunded":
        case "partial_payment":
        case "paid":
        case "accepted":
        case "completed":
        case "declined":
          return {
            className: "bg-[#f4fbf5] text-[#23B339]",
            Icon: GreenTrueIcon,
            label: "Completed",
          };
        case "overdue":
        case "past_due":
          return {
            className: "bg-[#fffbf2] text-[#FDB600]",
            Icon: PastDueIcon,
            label: "Past Due",
          };
        case "expired":
          return {
            className: "bg-[#fef2f2] text-[#FF0000]",
            Icon: ExpiredIcon,
            label: "Expired",
          };

        case "cancelled":
        case "canceled":
          return {
            className: "bg-[#fef2f2] text-[#FF0000]",
            Icon: CloseSmRedIcon,
            label: "Canceled",
          };
        default:
          return { className: "", Icon: null, label: "" };
      }
    } else {
      return { className: "", Icon: null, label: "" };
    }
  };

  const columns: ActionCenterColumn[] = [
    {
      id: "creation_date",
      name: "Created Date",
      minWidth: "130px",
      cell: (row: ActionRow) => {
        return (
          <div>
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : row?.insert_date ? (
              formatPaymentListDate(
                row?.insert_date,
                userData?.data?.accountData?.timezone,
                "short"
              )
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "amount",
      name: "Amount",
      minWidth: "130px",
      cell: (row: ActionRow) => {
        return (
          <div className="text-xl font-medium text-txtPrimary leading-normal">
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : row?.action === "payment_link" ? (
              // A payment link may legitimately carry no amount. An em dash is
              // the conventional "no value" marker in a table; "--" reads as
              // something broken.
              !Number(row?.total_amount) ? (
                "—"
              ) : (
                formatCurrency(row?.total_amount || 0)
              )
            ) : row?.total_amount ? (
              formatCurrency(row?.total_amount || 0)
            ) : (
              "$0.00"
            )}
          </div>
        );
      },
    },
    {
      id: "last_payment_status",
      name: "Status",
      minWidth: "150px",
      cell: (row: ActionRow) => {
        const status = row?.sourceStatus
          ? row?.sourceStatus?.toLowerCase()
          : null;
        const { className, Icon, label } = getStatusStylesAndIcon(status);

        return (
          <div className="relative">
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : status ? (
              <div className="flex items-center gap-[3px]">
                <div
                  className={`flex items-center py-[3px] px-[10px] gap-[6px] rounded-md text-sm pointer-events-none ${
                    className ? className : ""
                  }`}
                >
                  {Icon && <Icon />}
                  {label}
                </div>
                {status === "refunded" ||
                  (status === "partial_payment" && (
                    <CustomTooltip
                      maxwidth="264px"
                      place="top"
                      id={`action-custom-${row.id}`}
                      content={`<div class="font-normal text-xs leading-[18px] w-[250px]" >The payment for this invoice is refunded or partially refunded</div>`}
                      stopPropagation={true}
                    >
                      <div
                        className="w-fit cursor-pointer relative"
                        id={`action-custom-${row.id}`}
                      >
                        <InfoRedCircleIcon />
                      </div>
                    </CustomTooltip>
                  ))}
              </div>
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "Type_method",
      name: "Type",
      minWidth: "160px",
      cell: (row: ActionRow) => {
        return (
          <div>
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : row?.action === "payment_link" ? (
              "Payment Link"
            ) : row?.action === "invoice" ? (
              "Invoice"
            ) : row?.action === "quotes" ? (
              "Quote"
            ) : (
              "Payment Agreement"
            )}
          </div>
        );
      },
    },
    // {
    //   id: "actionId",
    //   name: "ID",
    //   minWidth: "110px",
    //   cell: (row: ActionRow) => {
    //     return (
    //       <div className="capitalize">
    //         {isTableLoading ? (
    //           <Skeleton width={100} />
    //         ) : row?.action === "invoice" ? (
    //           row?.invoice_number
    //         ) : row?.action === "quotes" ? (
    //           row?.quote_number
    //         ) : (
    //           row?.id
    //         )}
    //       </div>
    //     );
    //   },
    // },
    {
      id: "last_billing_date",
      name: "Due Date",
      minWidth: "130px",
      cell: (row: ActionRow) => {
        return (
          <div>
            {isTableLoading ? (
              <Skeleton width={100} />
            ) : getActionDueDate(row) ? (
              formatPaymentListDate(
                getActionDueDate(row),
                userData?.data?.accountData?.timezone,
                "short"
              )
            ) : (
              "--"
            )}
          </div>
        );
      },
    },
    {
      id: "id",
      name: "",
      right: "true",
      minWidth: "200px",
      cell: (row: ActionRow) => {
        if (isTableLoading) return <></>;

        const status = row?.sourceStatus
          ? row?.sourceStatus?.toLowerCase()
          : null;
        const { label } = getStatusStylesAndIcon(status);
        return (
          <div className="flex items-center gap-5">
            <div
              className="items-center gap-3 hidden icon-group"
              data-testid={tid(`row-actions-${getActionRowKey(row)}`)}
            >
              <div className="flex">
                {label.toLowerCase() !== "completed" && (
                  <Button
                    text="Open"
                    className="w-[96px] h-[43px] grid place-content-center text-base font-medium"
                    data-testid={tid(`open-action-${getActionRowKey(row)}`)}
                    aria-label={`Open ${getActionLabel(row)}`}
                    onClick={() => {
                      const id = getActionNavigationSlug(row, clusterId);

                      navigate(`/${row?.action}/${id}`, {
                        state: { action: row?.action, id: id },
                      });
                      dispatch(onSetSmallSidebar(true));
                    }}
                  />
                )}
              </div>
              <div
                role="button"
                tabIndex={0}
                aria-label={`Preview ${getActionLabel(row)}`}
                data-testid={tid(`preview-action-${getActionRowKey(row)}`)}
                onClick={() => {
                  if (
                    row?.action === "agreement" &&
                    label.toLowerCase() === "completed"
                  ) {
                    setIsSubscriptionPreview(true);
                    setIsSubscriptionPreviewId(row?.subscription_id || 0);
                  } else {
                    const now = new Date();
                    const prefix =
                      row?.action === "payment_link" ? "pl_" : "agr_";
                    const id =
                      row?.action === "payment_link" ||
                      row?.action === "agreement"
                        ? encode(
                            `${row?.id}_${clusterId}_${now.getTime()}`,
                            prefix
                          )
                        : row?.url_slug || "";
                    setIsPreview(true);
                    setPreviewType(row?.action);
                    setIsPreviewId(id);
                    setView(row?.action);
                  }
                }}
              >
                <EyeIcon
                  aria-hidden="true"
                  className="group"
                  _className="group-hover:!fill-iconObscure"
                />
              </div>
            </div>
            <Menu as="div" className="relative">
              <Menu.Button
                aria-label={`Open actions menu for ${getActionLabel(row)}`}
                data-testid={tid(`action-menu-button-${getActionRowKey(row)}`)}
              >
                <div className="hover:!bg-bgHover p-[9px] rounded-full">
                  <MoreIcon
                    aria-hidden="true"
                    className="group"
                    _className="group-hover:!fill-iconObscure"
                  />
                </div>
              </Menu.Button>

              <Menu.Items
                className="absolute w-[153px] -left-[130px] !bg-bgPrimary hover:!bg-bgPrimary z-[1] rounded-[7px] drop-shadow-lg top-[35px]"
                data-testid={tid(`action-menu-${getActionRowKey(row)}`)}
              >
                <Menu.Item
                  as="div"
                  className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-t-[7px]"
                  data-testid={tid(`menu-preview-${getActionRowKey(row)}`)}
                  onClick={() => {
                    if (
                      row?.action === "agreement" &&
                      label.toLowerCase() === "completed"
                    ) {
                      setIsSubscriptionPreview(true);
                      setIsSubscriptionPreviewId(row?.subscription_id || 0);
                    } else {
                      const id =
                        row?.action === "payment_link" ||
                        row?.action === "agreement"
                          ? encode(
                              `${row?.id}_${clusterId}_${new Date().getTime()}`,
                              row?.action === "payment_link" ? "pl_" : "agr_"
                            )
                          : row?.url_slug || "";
                      setIsPreview(true);
                      setPreviewType(row?.action);
                      setIsPreviewId(id);
                      setView(row?.action);
                    }
                  }}
                >
                  <EyeIcon width="16" height="16" aria-hidden="true" /> Preview
                </Menu.Item>
                {label.toLowerCase() === "open" && (
                  <Menu.Item
                    as="div"
                    className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary"
                    data-testid={tid(`menu-open-${getActionRowKey(row)}`)}
                    onClick={() => {
                      const id = getActionNavigationSlug(row, clusterId);

                      navigate(`/${row?.action}/${id}`, {
                        state: { action: row?.action, id: id },
                      });
                      dispatch(onSetSmallSidebar(true));
                    }}
                  >
                    <PayNowIcon aria-hidden="true" /> Pay now
                  </Menu.Item>
                )}
              </Menu.Items>
            </Menu>
          </div>
        );
      },
    },
  ];

  const selectedSlot = useMemo(() => {
    if (status.length) {
      if (
        status.length === 2 &&
        status.includes("Open") &&
        status.includes("Past Due")
      ) {
        return "Outstanding";
      } else if (status.length === 1 && status.includes("Canceled")) {
        return "cancelled";
      } else if (status.length === 1 && status.includes("Completed")) {
        return "completed";
      } else if (status.length === 1 && status.includes("Expired")) {
        return "Expired";
      }
      return "";
    } else {
      return "all";
    }
  }, [selectedFilter, status]);

  useEffect(() => {
    if (selectedFilter === "all" && !status?.length) return;

    switch (selectedFilter) {
      case "Outstanding":
        setStatus(["Open", "Past Due"]);
        break;
      case "cancelled":
        setStatus(["Canceled"]);
        break;
      case "completed":
        setStatus(["Completed"]);
        break;
      case "Expired":
        setStatus(["Expired"]);
        break;
      default:
        setStatus([]);
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter]);

  useEffect(() => {
    const methodFormatted = method?.map((data) => cardToShortname(data));
    if (!status?.length) {
      setSelectedFilter("all");
    }
    let arrStatus: string[];
    if (status?.length > 0) {
      // "Outstanding" is the server's name for exactly {Open, Past Due} — the
      // pair the Outstanding card selects. Match on membership, not on position:
      // the previous check fired whenever Open happened to be ticked first, or
      // whenever Past Due landed second, so pairs like Open + Completed were
      // silently sent as Outstanding and the customer saw the wrong list.
      // This mirrors the selectedSlot check above, which already used includes().
      const isOutstandingPair =
        status.length === 2 &&
        status.includes("Open") &&
        status.includes("Past Due");
      if (isOutstandingPair) {
        arrStatus = ["Outstanding"];
      } else {
        arrStatus = status.map((s: string) => {
          return s === "Open"
            ? "Opened"
            : s === "Paid"
              ? "paid"
              : s === "Past Due"
                ? "past_due"
                : s === "Canceled"
                  ? "Cancelled"
                  : s;
        });
      }
    } else {
      arrStatus = [];
    }
    let arr: string[];
    if (paymentType?.length > 0) {
      arr = paymentType.map((type: string) => {
        return type === "Payment Agreement"
          ? "agreement"
          : type === "Payment Link"
            ? "payment_link"
            : type === "Invoice"
              ? "invoice"
              : "quotes";
      });
    } else {
      arr = [];
    }
    setFilter({
      ...filter,
      skip: 0,
      method: method.length > 0 ? JSON.stringify(methodFormatted) : undefined,
      status: arrStatus?.length > 0 ? arrStatus : [],
      type: arr?.length > 0 ? arr : undefined,
      amount: amount ? JSON.stringify(amount) : undefined,
      dateFilter: dateFilter,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, method, amount, paymentType, dateFilter]);

  useEffect(() => {
    onGetActionList();
  }, [filter]);

  useEffect(() => {
    onGetActionCount();
  }, []);

  if (actionCount?.all === 0) {
    return (
      <div className="w-full lg:h-full h-[79%] ">
        <Header
          headerContent={
            <div className="w-full lg:text-[32px] sm:text-3xl text-2xl leading-[44px] text-txtPrimary font-medium -tracking-[0.72px] lg:p-0 p-5">
              Action Center
            </div>
          }
        />
        <div className="text-center lg:pt-[93px] lg:pb-[45px] h-full lg:px-0 px-5 grid place-items-center">
          <div className="h-fit w-fit flex items-center flex-col justify-center gap-10">
            <NoAction
              width={window.innerWidth < 500 ? 80 : 100}
              height={window.innerWidth < 500 ? 80 : 100}
            />
            <div>
              <p className="lg:text-2xl text-xl text-txtPrimary font-semibold mb-4">
                No pending actions found
              </p>
              <p className="lg:text-lg text-base text-txtParagraph">
                Your action center is clear! Enjoy the peace or make a payment?
              </p>
            </div>
            <Button
              text="Make Payment"
              className="lg:!px-11 lg:!py-[10px] !px-4 !py-2"
              data-testid={tid("empty-make-payment")}
              aria-label="Make a payment from action center"
              onClick={() => navigate(`/${cluster_id}/make-payment`)}
            />
          </div>
        </div>
      </div>
    );
  }

  const GetRow = (dataRow: ActionRow) => {
    const status = dataRow?.sourceStatus
      ? dataRow?.sourceStatus?.toLowerCase()
      : null;
    const { className, Icon, label } = getStatusStylesAndIcon(status);
    return (
      <div
        className="w-full py-3 flex flex-col items-start justify-start gap-[10px] relative"
        data-testid={tid(`mobile-row-${getActionRowKey(dataRow)}`)}
        aria-label={`${getActionLabel(dataRow)} action row`}
        onClick={() => {
          if (
            dataRow?.action === "agreement" &&
            label.toLowerCase() === "completed"
          ) {
            setIsSubscriptionPreview(true);
            setIsSubscriptionPreviewId(dataRow?.subscription_id || 0);
          } else {
            const id =
              dataRow?.action === "payment_link" ||
              dataRow?.action === "agreement"
                ? encode(
                    `${dataRow?.id}_${clusterId}_${new Date().getTime()}`,
                    dataRow?.action === "payment_link" ? "pl_" : "agr_"
                  )
                : dataRow?.url_slug || "";
            setIsPreview(true);
            if (dataRow?.action) {
              setPreviewType(dataRow.action);
              setView(dataRow.action);
            }
            setIsPreviewId(id);
          }
        }}
      >
        <div className="flex justify-between items-center w-full">
          <span className="text-xl font-medium">
            {dataRow?.action === "payment_link"
              ? !Number(dataRow?.total_amount)
                ? "—"
                : formatCurrency(dataRow?.total_amount || 0)
              : dataRow?.total_amount
                ? formatCurrency(dataRow?.total_amount || 0)
                : "$0.00"}
          </span>
          <span className="flex items-center justify-end gap-2">
            <div className="flex items-center gap-[3px]">
              <div
                className={`flex items-center py-[3px] px-[10px] gap-[6px] rounded-md text-sm ${
                  className ? className : ""
                }`}
              >
                {Icon && <Icon />}
                {label}
              </div>
              {status === "refunded" ||
                (status === "partial_payment" && (
                  <CustomTooltip
                    maxwidth="264px"
                    place="top"
                    id={`action-custom-${dataRow?.id}`}
                    content={`<div class="font-normal text-xs leading-[18px] w-[250px]" >The payment for this invoice is refunded or partially refunded</div>`}
                    stopPropagation={true}
                  >
                    <div
                      className="w-fit cursor-pointer relative"
                      id={`action-custom-${dataRow?.id}`}
                      data-testid={tid(
                        `mobile-row-status-tooltip-${getActionRowKey(dataRow)}`
                      )}
                    >
                      <InfoRedCircleIcon />
                    </div>
                  </CustomTooltip>
                ))}
            </div>
            <Menu as="div" className="relative grid place-items-center">
              <Menu.Button
                disabled={isTableLoading}
                onClick={(e) => e.stopPropagation()}
                aria-label={`Open actions menu for ${getActionLabel(dataRow)}`}
                data-testid={tid(
                  `mobile-action-menu-button-${getActionRowKey(dataRow)}`
                )}
              >
                <div className="hover:!bg-bgHover rounded-full">
                  <MoreIcon
                    aria-hidden="true"
                    className="group"
                    _className="group-hover:!fill-iconObscure"
                  />
                </div>
              </Menu.Button>

              <Menu.Items
                className="absolute w-[153px] -left-[130px] top-[30px] !bg-bgPrimary hover:!bg-bgPrimary z-[1] rounded-[7px] drop-shadow-lg "
                data-testid={tid(
                  `mobile-action-menu-${getActionRowKey(dataRow)}`
                )}
              >
                <Menu.Item
                  as="div"
                  className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary rounded-t-[7px]"
                  data-testid={tid(
                    `mobile-menu-preview-${getActionRowKey(dataRow)}`
                  )}
                  onClick={() => {
                    if (
                      dataRow?.action === "agreement" &&
                      label.toLowerCase() === "completed"
                    ) {
                      setIsSubscriptionPreview(true);
                      setIsSubscriptionPreviewId(dataRow?.subscription_id || 0);
                    } else {
                      const id =
                        dataRow?.action === "payment_link" ||
                        dataRow?.action === "agreement"
                          ? encode(
                              `${
                                dataRow?.id
                              }_${clusterId}_${new Date().getTime()}`,
                              dataRow?.action === "payment_link"
                                ? "pl_"
                                : "agr_"
                            )
                          : dataRow?.url_slug || "";
                      setIsPreview(true);
                      if (dataRow?.action) {
                        setPreviewType(dataRow.action);
                        setView(dataRow.action);
                      }
                      setIsPreviewId(id);
                    }
                  }}
                >
                  <EyeIcon width="16" height="16" aria-hidden="true" /> Preview
                </Menu.Item>
                {label.toLowerCase() === "open" && (
                  <Menu.Item
                    as="div"
                    className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary items-center font-normal leading-normal hover:!bg-bgSecondary"
                    data-testid={tid(
                      `mobile-menu-open-${getActionRowKey(dataRow)}`
                    )}
                    onClick={() => {
                      const id = getActionNavigationSlug(dataRow, clusterId);

                      navigate(`/${dataRow?.action}/${id}`, {
                        state: { action: dataRow?.action, id: id },
                      });
                    }}
                  >
                    <PayNowIcon aria-hidden="true" /> Pay now
                  </Menu.Item>
                )}
              </Menu.Items>
            </Menu>
          </span>
        </div>
        <div className="w-full flex justify-between gap-2 items-center">
          <div className="w-full flex flex-col gap-[10px]">
            <div className="w-full flex items-center text-txtParagraph text-sm">
              <span className="w-[110px] shrink-0">Created Date</span>
              <span className="flex-1 min-w-0 text-txtPrimary">
                {formatPaymentListDate(
                  dataRow?.insert_date,
                  userData?.data?.accountData?.timezone,
                  "short"
                )}
              </span>
            </div>
            <div className="w-full flex items-center text-txtParagraph text-sm">
              <span className="w-[110px] shrink-0">Type</span>
              <span className="flex-1 min-w-0 text-txtPrimary">
                {dataRow?.action === "payment_link"
                  ? "Payment Link"
                  : dataRow?.action === "invoice"
                    ? "Invoice"
                    : dataRow?.action === "quotes"
                      ? "Quote"
                      : "Payment Agreement"}
              </span>
            </div>
            {/* <div className="w-full flex items-center text-txtParagraph text-sm">
              <span className="w-[110px] shrink-0">ID</span>
              <span className="flex-1 min-w-0 text-txtPrimary">
                {dataRow?.action === "invoice"
                  ? dataRow?.invoice_number
                  : dataRow?.action === "quotes"
                    ? dataRow?.quote_number
                    : dataRow?.id}
              </span>
            </div> */}
            <div className="w-full flex items-center text-txtParagraph text-sm">
              <span className="w-[110px] shrink-0">Due Date</span>
              <span className="flex-1 min-w-0 text-txtPrimary">
                {formatPaymentListDate(
                  getActionDueDate(dataRow),
                  userData?.data?.accountData?.timezone,
                  "short"
                )}
              </span>
            </div>
          </div>
          {label.toLowerCase() !== "completed" && (
            <div className="h-full w-fit flex items-center justify-center">
              <Button
                text="Open"
                className="cursor-pointer w-fit py-[6px] px-[16px] rounded-md text-base text-white leading-6 font-medium focus:outline-none"
                data-testid={tid(
                  `mobile-open-action-${getActionRowKey(dataRow)}`
                )}
                aria-label={`Open ${getActionLabel(dataRow)}`}
                onClick={() => {
                  const id = getActionNavigationSlug(dataRow, clusterId);

                  navigate(`/${dataRow?.action}/${id}`, {
                    state: { action: dataRow?.action, id: id },
                  });
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className="w-full lg:h-full lg:block flex flex-col lg:overflow-hidden"
      {...testAttrs("page", "Action center")}
    >
      <Header
        headerContent={
          <div className="w-full h-fit lg:text-[32px] sm:text-3xl text-2xl leading-[44px] text-txtPrimary font-medium -tracking-[0.72px] lg:p-0 p-5">
            Action Center
          </div>
        }
      />

      <div className="lg:pt-[93px] h-full lg:overflow-y-auto">
        <div className="lg:px-10 lg:pt-10 h-full lg:overflow-y-auto">
          {/* CARDS SECTION */}
          <div
            className="lg:px-0 px-5 flex items-center lg:gap-[28px] gap-5 lg:flex-nowrap flex-wrap lg:overflow-x-auto"
            {...testAttrs("summary-cards", "Action center summary")}
          >
            {/* All */}
            <div
              className={`${
                selectedSlot === "all" ? "border-blue" : "border-strokePrimary"
              } p-4 border-[1.5px] rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full h-[100px] hover:border-blue flex flex-col justify-between items-start lg:min-w-[172px]`}
              role="button"
              tabIndex={0}
              data-testid={tid("summary-card-all")}
              aria-label="Show all actions"
              onClick={() => {
                setSelectedFilter("all");
                setStatus([]);
              }}
            >
              <div className="lg:text-2xl text-xl  leading-normal font-medium text-txtPrimary">
                All
              </div>
              <div className="text-lg leading-normal text-txtSecondary animate__animated animate__fadeIn">
                {isLoading ? (
                  <Skeleton width={27} height={23} />
                ) : (
                  actionCount?.all || 0
                )}
              </div>
            </div>
            {/* Outstanding */}
            <div
              className={`${
                selectedSlot === "Outstanding"
                  ? "border-blue"
                  : "border-strokePrimary"
              } p-4 border-[1.5px] rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full h-[100px] hover:border-blue flex flex-col justify-between items-start  lg:min-w-[172px]`}
              role="button"
              tabIndex={0}
              data-testid={tid("summary-card-outstanding")}
              aria-label="Show outstanding actions"
              onClick={() => {
                setSelectedFilter("Outstanding");
              }}
            >
              <div className="lg:text-2xl text-xl  leading-normal font-medium text-txtPrimary">
                Outstanding
              </div>
              <div className="text-lg leading-normal text-txtSecondary animate__animated animate__fadeIn">
                {isLoading ? (
                  <Skeleton width={27} height={23} />
                ) : (
                  actionCount?.outstanding || 0
                )}
              </div>
            </div>
            {/* Completed */}
            <div
              className={`${
                selectedSlot === "completed"
                  ? "border-blue"
                  : "border-strokePrimary"
              } p-4 border-[1.5px] rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full h-[100px] hover:border-blue flex flex-col justify-between items-start  lg:min-w-[172px]`}
              role="button"
              tabIndex={0}
              data-testid={tid("summary-card-completed")}
              aria-label="Show completed actions"
              onClick={() => {
                setSelectedFilter("completed");
              }}
            >
              <div className="lg:text-2xl text-xl  leading-normal font-medium text-txtPrimary">
                Completed
              </div>
              <div className="text-lg leading-normal text-txtSecondary animate__animated animate__fadeIn">
                {isLoading ? (
                  <Skeleton width={27} height={23} />
                ) : (
                  actionCount?.completed || 0
                )}
              </div>
            </div>
            {/* Expired */}
            <div
              className={`${
                selectedSlot === "Expired"
                  ? "border-blue"
                  : "border-strokePrimary"
              } p-4 border-[1.5px] rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full h-[100px] hover:border-blue flex flex-col justify-between items-start  lg:min-w-[172px]`}
              role="button"
              tabIndex={0}
              data-testid={tid("summary-card-expired")}
              aria-label="Show expired actions"
              onClick={() => {
                setSelectedFilter("Expired");
              }}
            >
              <div className="lg:text-2xl text-xl  leading-normal font-medium text-txtPrimary">
                Expired
              </div>
              <div className="text-lg leading-normal text-txtSecondary animate__animated animate__fadeIn">
                {isLoading ? (
                  <Skeleton width={27} height={23} />
                ) : (
                  actionCount?.expired || 0
                )}
              </div>
            </div>
            {/* Canceled */}
            <div
              className={`${
                selectedFilter === "cancelled"
                  ? "border-blue"
                  : "border-strokePrimary"
              } p-4 border-[1.5px] rounded-xl bg-bgPrimary cursor-pointer lg:w-full sm:w-[calc(50%-10px)] w-full h-[100px] hover:border-blue flex flex-col justify-between items-start  lg:min-w-[172px]`}
              role="button"
              tabIndex={0}
              data-testid={tid("summary-card-canceled")}
              aria-label="Show canceled actions"
              onClick={() => {
                setSelectedFilter("cancelled");
              }}
            >
              <div className="lg:text-2xl text-xl  leading-normal font-medium text-txtPrimary">
                Canceled
              </div>
              <div className="text-lg leading-normal text-txtSecondary animate__animated animate__fadeIn">
                {isLoading ? (
                  <Skeleton width={27} height={23} />
                ) : (
                  actionCount?.canceled || 0
                )}
              </div>
            </div>
          </div>

          {/* FILTER SECTION */}
          <div
            ref={filterScrollRef}
            /* Mobile wraps onto two rows per the design. The chips previously sat
               in one sideways-scrolling strip, which cut "Type" off at the edge
               of the screen where it read as broken and was easy to miss.
               Desktop keeps the single row. */
            className="pt-[30px] flex items-center gap-3 flex-wrap lg:flex-nowrap lg:overflow-x-auto lg:px-0 px-5 scrollbar-hide"
            {...testAttrs("filters", "Action center filters")}
          >
            <div className="w-[calc(50%-6px)] lg:w-fit" ref={dateFilterRef}>
              <DateFilter
                options={FilterDateOptions}
                dateInput1={dateInput1}
                dateInput2={dateInput2}
                setDateInput1={setDateInput1}
                setDateInput2={setDateInput2}
                label="Date"
                setdateFilter={setdateFilter}
                onOpen={() => {
                  if (filterScrollRef.current && dateFilterRef.current) {
                    filterScrollRef.current.scrollTo({
                      left: 0,
                      behavior: "smooth",
                    });
                  }
                }}
              />
            </div>
            <div className="w-[calc(50%-6px)] lg:w-fit" ref={amountFilterRef}>
              <AmountFilter
                options={FilterAmountOptions}
                amount={amount}
                setAmount={setAmount}
                label="Amount"
                onOpen={() => {
                  if (filterScrollRef.current && amountFilterRef.current) {
                    filterScrollRef.current.scrollTo({
                      left:
                        amountFilterRef.current.offsetLeft -
                        (filterScrollRef.current.offsetLeft || 0),
                      behavior: "smooth",
                    });
                  }
                }}
              />
            </div>
            <div className="w-[calc(50%-6px)] lg:w-fit" ref={statusFilterRef}>
              <SelectAllDropDown
                options={ActionCenterStatusOptions}
                checked={status}
                setChecked={setStatus}
                label="Status"
                onOpen={() => {
                  if (filterScrollRef.current && statusFilterRef.current) {
                    filterScrollRef.current.scrollTo({
                      left:
                        statusFilterRef.current.offsetLeft -
                        (filterScrollRef.current.offsetLeft || 0),
                      behavior: "smooth",
                    });
                  }
                }}
              />
            </div>
            <div className="w-[calc(50%-6px)] lg:w-fit" ref={methodFilterRef}>
              <SelectAllDropDown
                options={TypeFilter}
                checked={paymentType}
                setChecked={setPaymentType}
                label="Type"
                onOpen={() => {
                  if (filterScrollRef.current && methodFilterRef.current) {
                    filterScrollRef.current.scrollTo({
                      left:
                        methodFilterRef.current.offsetLeft -
                        (filterScrollRef.current.offsetLeft || 0),
                      behavior: "smooth",
                    });
                  }
                }}
              />
            </div>
          </div>

          {/* TABLE TO DISPLAY DATA */}
          <div className="pt-[30px]">
            <CustomTable
              data-testid={tid("table")}
              ariaLabel="Action center actions table"
              columns={columns}
              data={actionList?.actionListData}
              getRowKey={getActionRowKey}
              getRowTestId={(row: ActionRow) => `row-${getActionRowKey(row)}`}
              fixedHeaderScrollHeight="calc(var(--shell-vh) - 452px)"
              perPage={filter?.limit}
              currentPage={(filter?.skip + 20) / 20}
              totalCount={actionList?.totalCount as number}
              isTableLoading={isTableLoading}
              onNextClick={(e) => {
                setFilter({
                  ...filter,
                  skip: filter?.skip + 20,
                });
              }}
              onPreviousClick={(e) => {
                setFilter({
                  ...filter,
                  skip: filter?.skip - 20,
                });
              }}
              GetRow={GetRow}
              onRowClick={(row: ActionRow, e: React.MouseEvent) => {
                const status = row?.sourceStatus
                  ? row?.sourceStatus?.toLowerCase()
                  : null;
                const { className, Icon, label } =
                  getStatusStylesAndIcon(status);

                if (
                  row?.action === "agreement" &&
                  label.toLowerCase() === "completed"
                ) {
                  setIsSubscriptionPreview(true);
                  setIsSubscriptionPreviewId(row?.subscription_id || 0);
                } else {
                  const id =
                    row?.action === "payment_link" ||
                    row?.action === "agreement"
                      ? encode(
                          `${row?.id}_${clusterId}_${new Date().getTime()}`,
                          row?.action === "payment_link" ? "pl_" : "agr_"
                        )
                      : row?.url_slug || "";
                  setIsPreview(true);
                  setPreviewType(row?.action);
                  setIsPreviewId(id);
                  setView(row?.action);
                }
              }}
            />
          </div>

          {/* PREVIEW OF ACTION */}
          <div
            className="contents"
            {...testAttrs("drawers", "Action center preview drawers")}
          >
            <Drawer
              direction="right"
              open={
                isPreview &&
                (previewType === "payment_link" || previewType === "agreement")
              }
              size={isLessThan590 ? `100vw` : "590px"}
              onClose={() => {
                setIsPreview(false);
                setPreviewType("");
                setIsPreviewId("");
              }}
              overlayColor="#000"
              lockBackgroundScroll={true}
              enableOverlay={true}
            >
              <div
                className="contents"
                {...testAttrs("payment-link-agreement-drawer")}
              >
                <ActionPreview
                  id={isPreviewId}
                  setId={setIsPreviewId}
                  show={
                    isPreview &&
                    (previewType === "payment_link" ||
                      previewType === "agreement")
                  }
                  setShow={setIsPreview}
                  view={view}
                  setView={setView}
                />
              </div>
            </Drawer>
            <Drawer
              direction="right"
              open={isPreview && previewType === "quotes"}
              size={isLessThan590 ? `100vw` : "590px"}
              onClose={() => {
                setIsPreview(false);
                setPreviewType("");
                setIsPreviewId("");
              }}
              overlayColor="#000"
              lockBackgroundScroll={true}
            >
              <div className="contents" {...testAttrs("quote-drawer")}>
                <QuotesPreview
                  id={isPreviewId}
                  slug={isPreviewId}
                  setId={setIsPreviewId}
                  show={isPreview && previewType === "quotes"}
                  setShow={setIsPreview}
                  isSlug={true}
                  fetchDetails={() => {
                    onGetActionList();
                    onGetActionCount();
                  }}
                />
              </div>
            </Drawer>
            <Drawer
              direction="right"
              open={isPreview && previewType === "invoice"}
              size={isLessThan590 ? `100vw` : "590px"}
              onClose={() => {
                setIsPreview(false);
                setPreviewType("");
                setIsPreviewId("");
              }}
              overlayColor="#000"
              lockBackgroundScroll={true}
            >
              <div className="contents" {...testAttrs("invoice-drawer")}>
                <InvoicePreview
                  id={isPreviewId}
                  slug={isPreviewId}
                  setId={setIsPreviewId}
                  setIsPreview={setIsPreview}
                  isPreview={isPreview && previewType === "invoice"}
                  isSlug={true}
                />
              </div>
            </Drawer>

            <Drawer
              direction="right"
              open={isSubscriptionPreview}
              size={isLessThan590 ? `100vw` : "590px"}
              onClose={() => {
                setIsSubscriptionPreview(false);
                setIsSubscriptionPreviewId(0);
              }}
              overlayColor="#000"
              lockBackgroundScroll={true}
            >
              <div className="contents" {...testAttrs("subscription-drawer")}>
                <SubscriptionsPreview
                  id={isSubscriptionPreviewId}
                  setId={setIsSubscriptionPreviewId}
                  show={isSubscriptionPreview}
                  setShow={setIsSubscriptionPreview}
                  setHandleDrawerSize={() => {}}
                  isApiCall={true}
                  isScheduled={false}
                  isActionCenter={true}
                />
              </div>
            </Drawer>
          </div>
        </div>
      </div>
    </div>
  );
};

const ActionCenter: React.FC = () => (
  <TestIdScope scope="action-center">
    <ActionCenterContent />
  </TestIdScope>
);

export default ActionCenter;
